import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Crown, CheckCircle2, Loader2, LogIn } from "lucide-react";

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

/**
 * Public-facing tier picker for a show. Shows Stripe and/or PayPal
 * subscribe buttons depending on the admin's site setting
 * (`payment_provider`: "stripe" | "paypal" | "both").
 */
export default function MembershipTiers({ showId }) {
  const { user } = useAuth();
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(null); // "{tierId}:{provider}"
  const [provider, setProvider] = useState("stripe"); // default
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);

  useEffect(() => {
    if (!showId) return;
    (async () => {
      try {
        const [tiersR, cfgR] = await Promise.all([
          api.get(`/shows/${showId}/tiers`),
          api.get("/config/paypal").catch(() => ({ data: { payment_provider: "stripe", payments_enabled: true } })),
        ]);
        setTiers(tiersR.data.tiers || []);
        setProvider(cfgR.data?.payment_provider || "stripe");
        // payments_enabled is exposed via the same public config endpoint
        setPaymentsEnabled(cfgR.data?.payments_enabled !== false);
      } catch {
        setTiers([]);
      }
    })();
  }, [showId]);

  if (tiers === null) return null;
  if (tiers.length === 0) return null;

  const subscribe = async (tier, method) => {
    if (!user) {
      toast.info("Sign in to become a member.");
      window.location.href = "/login";
      return;
    }
    if (user.is_banned) {
      toast.error("Banned accounts cannot subscribe.");
      return;
    }
    const key = `${tier.id}:${method}`;
    setBusy(key);
    try {
      const endpoint = method === "paypal" ? "/paypal/checkout" : "/payments/checkout";
      const { data } = await api.post(endpoint, {
        tier_id: tier.id,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Checkout failed");
      setBusy(null);
    }
  };

  const accents = [
    { label: "TIER 1", ring: "border-[var(--ff-border)]" },
    { label: "TIER 2 · POPULAR", ring: "border-[var(--ff-red)]" },
    { label: "TIER 3", ring: "border-yellow-500/60" },
  ];

  const showStripe = paymentsEnabled && (provider === "stripe" || provider === "both");
  const showPayPal = paymentsEnabled && (provider === "paypal" || provider === "both");

  return (
    <div className="mb-4" data-testid="membership-tiers">
      <div className="flex items-center gap-3 mb-6">
        <Crown className="text-[var(--ff-red)]" size={22} />
        <h2 className="font-display text-3xl">Support This Show</h2>
      </div>
      <p className="text-[var(--ff-text-dim)] text-sm font-mono-ui mb-6 max-w-2xl">
        Monthly membership goes directly to the hosts. Cancel anytime.
      </p>
      {!paymentsEnabled && (
        <div className="mb-6 border border-yellow-500/40 bg-yellow-500/5 p-4 text-sm font-mono-ui text-yellow-300" data-testid="tiers-payments-paused">
          Memberships are temporarily paused — please check back soon.
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {tiers.map((t, i) => {
          const a = accents[Math.min(i, accents.length - 1)];
          const stripeReady = !!t.stripe_price_id;
          const paypalReady = !!t.paypal_plan_id;
          return (
            <div
              key={t.id}
              className={`surface-card p-6 border ${a.ring} flex flex-col`}
              data-testid={`tier-card-${t.tier_order}`}
            >
              <div className="label-eyebrow text-[var(--ff-red)] mb-2">{a.label}</div>
              <div className="font-display text-2xl mb-1">{t.name}</div>
              <div className="font-mono-ui text-3xl mb-4">
                {money(t.price_cents)}
                <span className="text-sm text-[var(--ff-text-dim)]"> / mo</span>
              </div>
              {t.perks && t.perks.length > 0 && (
                <ul className="space-y-2 mb-6 flex-1">
                  {t.perks.map((p, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 size={14} className="text-green-400 mt-0.5 shrink-0" /> <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="space-y-2 mt-auto">
                {showStripe && (
                  <button
                    onClick={() => subscribe(t, "stripe")}
                    disabled={busy === `${t.id}:stripe` || !stripeReady}
                    className="btn-primary w-full justify-center"
                    title={stripeReady ? "" : "Stripe price not synced yet — admin must re-save the tier."}
                    data-testid={`tier-subscribe-stripe-${t.tier_order}`}
                  >
                    {busy === `${t.id}:stripe`
                      ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</>
                      : user ? <>Subscribe with Card →</> : <><LogIn size={14} /> Sign in to subscribe</>}
                  </button>
                )}
                {showPayPal && (
                  <button
                    onClick={() => subscribe(t, "paypal")}
                    disabled={busy === `${t.id}:paypal` || !paypalReady}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors bg-[#003087] text-white border-[#003087] hover:bg-[#001f5b] disabled:opacity-40"
                    title={paypalReady ? "" : "PayPal plan not synced yet — admin must re-save the tier."}
                    data-testid={`tier-subscribe-paypal-${t.tier_order}`}
                  >
                    {busy === `${t.id}:paypal`
                      ? <><Loader2 size={14} className="animate-spin" /> Redirecting…</>
                      : user ? <>Pay with PayPal</> : <><LogIn size={14} /> Sign in to subscribe</>}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)] mt-4 text-center">
        Payments processed by {showStripe && showPayPal ? "Stripe and PayPal" : showPayPal ? "PayPal" : "Stripe"}. See <Link to="/memberships" className="underline">your memberships</Link>.
      </div>
    </div>
  );
}
